import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// Notistack
import { useSnackbar } from 'notistack';

// MUI
import { Grid, Divider, Fab, Dialog, DialogContent, DialogActions, Button, CircularProgress } from '@mui/material';
import { Edit as SignIcon } from '@mui/icons-material';

// Apollo
import { useMutation } from '@apollo/client';
import { UPLOAD_LEARNER_WORK_SITUATION_SIGNATURE } from './gql-upload-learner-signatures';

// Services
import { ILearnerWorkSituation, IWorkSituation } from 'services/interfaces';
import { getErrorExtensions } from 'services/utils';

// Components
import Title from 'components/title';
import SignCard from 'components/sign-card';
import PelletCard from 'components/pellet-card';
import { isCanvasBlank } from 'components/sign-card/utils';

// ---------------------------------------------------------------------------------------------------- \\
// ------------------------------------- UPLOAD LEARNERS SIGNATURES ------------------------------------- \\
// ---------------------------------------------------------------------------------------------------- \\

const UploadLearnerSignatures = ({
    workSituation,
    refetch,
} : {
    workSituation: IWorkSituation | undefined,
    refetch: any,
}) => {

    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [learnerWS, setLearnerWS] = useState<ILearnerWorkSituation | null>(null);

    return (
        <>
            <PelletCard
                paddingTop={0}
                startIcon={<SignIcon color="success" />}
                summary={(
                    <Grid item xs>
                        {t('learner_signatures')}
                    </Grid>
                )}
            >
                <table>
                    <thead>
                        <tr>
                            <td className="table-td">
                                <Title>
                                    {t('name')}
                                </Title>
                            </td>
                            <td className="table-td">
                                <Title>
                                    {t('status')}
                                </Title>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {(
                            workSituation?.learnerWorkSituations
                            && workSituation.learnerWorkSituations.length > 0
                        ) && (
                            workSituation.learnerWorkSituations.map(lws => (
                                <tr key={lws.id}>
                                    <td className="table-td">
                                        {lws.learner?.fullName}
                                    </td>
                                    <td className="table-td">
                                        {lws.signatureUrl ? t('signed') : (workSituation?.signOffSheetUrl ? t('see_sign_off_sheet') : t('waiting_signature'))}
                                    </td>
                                    <td className="table-td">
                                        <Fab
                                            onClick={() => { setLearnerWS(lws); setOpen(true); }}
                                            color="success"
                                        >
                                            <SignIcon />
                                        </Fab>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </PelletCard>
            {(open && learnerWS) && (
                <SignatureDialog
                    learnerWorkSituation={learnerWS}
                    onClose={() => setOpen(false)}
                    refetch={() => { setOpen(false); refetch(); }}
                />
            )}
        </>
    );
};

// ---------------------------------------------------------------------------------------------------- \\
// ----------------------------------------- SIGNATURE DIALOG ----------------------------------------- \\
// ---------------------------------------------------------------------------------------------------- \\

const SignatureDialog = ({
    learnerWorkSituation,
    onClose,
    refetch,
} : {
    learnerWorkSituation: ILearnerWorkSituation,
    onClose: () => void,
    refetch: any,
}) => {

    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [uploadLearnerSignature, { loading, error, data }] = useMutation(UPLOAD_LEARNER_WORK_SITUATION_SIGNATURE);

    const onUploadTrainerSignature = () => {
        if (isCanvasBlank()) {
            enqueueSnackbar(t('missing_signature'), { variant: 'warning' });
        }
        else {
            let canvas = document.getElementById('signCanvas') as HTMLCanvasElement;
            canvas.toBlob(blob => {
                uploadLearnerSignature({
                    variables: {
                        id: learnerWorkSituation.id,
                        file: blob,
                    },
                });
            });
        }
    };

    useEffect(() => {
        if (data?.uploadLearnerWorkSituationSignature) {
            enqueueSnackbar(t('added_successfully'), { variant: 'success' });
            refetch();
        }
        if (error) {
            enqueueSnackbar(getErrorExtensions(error), { variant: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, error]);

    return (
        <Dialog
            id="dialog"
            open
            onClose={onClose}
            fullScreen
        >
            <DialogContent style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="dialog-title">
                    {t('signature')} {t('of.1')} {learnerWorkSituation?.learner?.fullName}
                </div>
                <SignCard />
            </DialogContent>
            <Divider />
            <DialogActions>
                <Button
                    onClick={onClose}
                    color="inherit"
                >
                    {t('close')}
                </Button>
                <Button
                    onClick={onUploadTrainerSignature}
                    disabled={loading}
                    endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <></>}
                    color="success"
                >
                    {t('validate')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadLearnerSignatures;
